import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    BooleanIcon,
    CategoryIcon,
    DecimalIcon,
    IntegerIcon,
    MultiChoiceIcon,
    SearchIcon, SubModelIcon,
    TextIcon
} from 'assests/GmsIcons';
import Typography from 'Components/DesignSystem/Typography/Typography';

type SelectedFieldInfo = {
    subModelId: string;
    fieldId: string;
}

type FieldMappingDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    onFieldSelected: (fieldId: string, subModelId: string | undefined, displayName: string) => void;
    userProjectDataModels: any;
    selectedFields?: SelectedFieldInfo[];
    // Restrict the tree to fields whose (lowercased) type is in this list, e.g. ['boolean']
    allowedTypes?: string[];
    title?: string;
}

type FieldItem = {
    id: string;
    name: string;
    type: string;
    path: string;
    displayPath: string;
    submodelId?: string;
    submodelName?: string;
}

type SubmodelItem = {
    id: string;
    name: string;
    type: string;
    fields: FieldItem[];
    isExpanded: boolean;
    hasFields: boolean;
}

// Chevron icon for expand/collapse
const ChevronRightIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 5L11 9L7 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const ChevronDownIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 7L9 11L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Box icon for submodels (matching Figma design)
const BoxIcon = ({ disabled = false }: { disabled?: boolean }) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.5 8V4C10.5 3.73478 10.3946 3.48043 10.2071 3.29289C10.0196 3.10536 9.76522 3 9.5 3H2.5C2.23478 3 1.98043 3.10536 1.79289 3.29289C1.60536 3.48043 1.5 3.73478 1.5 4V8C1.5 8.26522 1.60536 8.51957 1.79289 8.70711C1.98043 8.89464 2.23478 9 2.5 9H9.5C9.76522 9 10.0196 8.89464 10.2071 8.70711C10.3946 8.51957 10.5 8.26522 10.5 8Z" stroke={disabled ? "#DADBDD" : "#0747A6"} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6 3V9" stroke={disabled ? "#DADBDD" : "#0747A6"} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1.5 6H10.5" stroke={disabled ? "#DADBDD" : "#0747A6"} strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// List icon for multi-choice/list fields
const ListFieldIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 3H10.5" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 6H10.5" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 9H10.5" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1.5 3H1.505" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1.5 6H1.505" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1.5 9H1.505" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Grid icon for reference/category fields
const GridFieldIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1.5" y="1.5" width="3.5" height="3.5" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="7" y="1.5" width="3.5" height="3.5" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="1.5" y="7" width="3.5" height="3.5" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="7" y="7" width="3.5" height="3.5" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const FieldMappingDrawer = ({
                                isOpen,
                                onClose,
                                onFieldSelected,
                                userProjectDataModels,
                                selectedFields = [],
                                allowedTypes,
                                title = 'Select Field'
                            }: FieldMappingDrawerProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedSubmodels, setExpandedSubmodels] = useState<Set<string>>(new Set());
    const [selectedField, setSelectedField] = useState<FieldItem | null>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Reset state when popup closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setSelectedField(null);
        }
    }, [isOpen]);

    // Parse model data into field list
    const parsedData = useMemo(() => {
        const result: {
            rootFields: FieldItem[];
            submodels: SubmodelItem[];
        } = {
            rootFields: [],
            submodels: []
        };

        if (!userProjectDataModels?.project) return result;

        const project = userProjectDataModels.project;

        // Parse root-level fields
        if (project.fields) {
            project.fields.forEach((field: any) => {
                if (field?.data?.name) {
                    const type = field.type?.toLowerCase() || 'field';
                    if (allowedTypes && !allowedTypes.includes(type)) return;
                    result.rootFields.push({
                        id: field.id,
                        name: field.data.name,
                        type,
                        path: field.id,
                        displayPath: field.data.name
                    });
                }
            });
        }

        // Parse submodels and their fields
        if (project.dataModels) {
            project.dataModels.forEach((dataModel: any) => {
                const submodel: SubmodelItem = {
                    id: dataModel.id,
                    name: dataModel.data?.name || 'Unnamed Submodel',
                    type: 'submodel',
                    fields: [],
                    isExpanded: false,
                    hasFields: false
                };

                // Parse fields within submodel
                if (dataModel.data?.fields) {
                    dataModel.data.fields.forEach((field: any) => {
                        if (field?.name) {
                            const type = field.type?.toLowerCase() || 'field';
                            if (allowedTypes && !allowedTypes.includes(type)) return;
                            submodel.fields.push({
                                id: field.id,
                                name: field.name,
                                type,
                                path: `${dataModel.id}.${field.id}`,
                                displayPath: `${submodel.name}.${field.name}`,
                                submodelId: dataModel.id,
                                submodelName: submodel.name
                            });
                        }
                    });
                }

                submodel.hasFields = submodel.fields.length > 0;
                result.submodels.push(submodel);
            });
        }

        return result;
    }, [userProjectDataModels, allowedTypes]);

    // Filter fields based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return parsedData;

        const lowerSearch = searchTerm.toLowerCase();

        const filteredRootFields = parsedData.rootFields.filter(
            field => field.name.toLowerCase().includes(lowerSearch)
        );

        const filteredSubmodels = parsedData.submodels.map(submodel => ({
            ...submodel,
            fields: submodel.fields.filter(
                field => field.name.toLowerCase().includes(lowerSearch) ||
                    submodel.name.toLowerCase().includes(lowerSearch)
            )
        })).filter(submodel =>
            submodel.fields.length > 0 ||
            submodel.name.toLowerCase().includes(lowerSearch)
        );

        return {
            rootFields: filteredRootFields,
            submodels: filteredSubmodels
        };
    }, [parsedData, searchTerm]);

    const toggleSubmodel = (submodelId: string) => {
        setExpandedSubmodels(prev => {
            const next = new Set(prev);
            if (next.has(submodelId)) {
                next.delete(submodelId);
            } else {
                next.add(submodelId);
            }
            return next;
        });
    };

    const collapseAll = useCallback(() => {
        setExpandedSubmodels(new Set());
    }, []);

    const handleFieldSelect = (field: FieldItem) => {
        setSelectedField(field);
        onFieldSelected(field.id, field.submodelId, field.displayPath);
    };

    // Check if a field is already selected by another parameter
    const isFieldAlreadySelected = useCallback((fieldId: string, subModelId?: string): boolean => {
        return selectedFields.some(sf =>
            sf.fieldId === fieldId && sf.subModelId === (subModelId || '')
        );
    }, [selectedFields]);

    const getFieldIcon = (type: string) => {
        switch (type) {
            case 'multi-choice':
            case 'list':
                return <MultiChoiceIcon />;
            case 'text':
                return <TextIcon />;
            case 'integer':
                return <IntegerIcon />;
            case 'decimal':
                return <DecimalIcon />;
            case 'boolean':
                return <BooleanIcon />;
            default:
                return <CategoryIcon />; // Fallback icon
        }
    };

    if (!isOpen) return null;

    return (
        <div id="field-mapping-drawer-overlay" className="tw-fixed tw-inset-0 tw-z-50">
            {/* Transparent backdrop for click detection */}
            <div className="tw-absolute tw-inset-0" />

            {/* Popup Panel - positioned on the right side */}
            <div
                id="field-mapping-drawer"
                ref={popupRef}
                className="tw-absolute tw-p-3 tw-bg-white tw-right-0 tw-bottom-0 tw-top-[58px] tw-w-[307px] tw-shadow-lg tw-flex tw-flex-col tw-overflow-hidden tw-border-l tw-border-grey-200"
            >
                {/* Header - Select Field */}
                <div className="tw-px-0 tw-py-2.5 tw-bg-[#FDFDFD] tw-rounded-t-sm">
                    <Typography variant="title-header" className="tw-text-grey-600">
                        {title}
                    </Typography>
                </div>

                {/* Search Input */}
                <div id="field-mapping-search-container" className="tw-flex tw-items-center tw-bg-[#FDFDFD] tw-border tw-border-grey-200 tw-h-8 tw-px-2.5 tw-rounded-sm">
                    <span className="tw-text-grey-600 tw-w-[18px] tw-h-[18px] tw-flex tw-items-center tw-justify-center">
                        <SearchIcon />
                    </span>
                    <input data-testid="inputparameter-fieldmappingdrawer-input-1"
                        id="field-mapping-search-input"
                        type="search"
                        className="tw-flex-1 tw-h-8 tw-border-0 tw-text-caption-metadata tw-py-2 tw-px-2.5 tw-bg-transparent tw-text-grey-900 tw-placeholder-grey-500 focus:tw-outline-none"
                        placeholder="Search"
                        aria-label="Search"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Collapse All Button */}
                <div className="tw-flex tw-justify-end tw-py-1">
                    <button data-testid="inputparameter-fieldmappingdrawer-button-2"
                        id="field-mapping-collapse-all-btn"
                        onClick={collapseAll}
                        className="tw-bg-white tw-text-blue-900 tw-text-[10px] tw-font-semibold tw-px-5 tw-py-2 tw-rounded-sm hover:tw-bg-blue-100 tw-transition-colors"
                    >
                        Collapse All
                    </button>
                </div>

                {/* Field Tree */}
                <div id="field-mapping-tree" className="tw-flex-1 tw-overflow-y-auto">
                    {/* Submodels */}
                    {filteredData.submodels.map(submodel => {
                        const isExpanded = expandedSubmodels.has(submodel.id);
                        const hasFields = submodel.hasFields;
                        const isDisabled = !hasFields;

                        return (
                            <div key={submodel.id} id={`field-mapping-submodel-${submodel.id}`}>
                                {/* Submodel Header Row */}
                                <div
                                    className={`tw-flex tw-items-center tw-ps-2 tw-h-7 tw-border tw-border-grey-200/50 ${
                                        isExpanded ? 'tw-bg-grey-100' : 'tw-bg-white'
                                    }`}
                                >
                                    {/* Submodel content */}
                                    <button data-testid="inputparameter-fieldmappingdrawer-button-3"
                                        id={`field-mapping-submodel-btn-${submodel.id}`}
                                        onClick={() => hasFields && toggleSubmodel(submodel.id)}
                                        disabled={isDisabled}
                                        className={`tw-flex-1 tw-flex tw-items-center tw-gap-1.5 tw-h-full tw-pr-2 ${
                                            isDisabled ? 'tw-cursor-default' : 'tw-cursor-pointer'
                                        }`}
                                    >
                                        <SubModelIcon />
                                        <span className={`tw-text-caption-metadata tw-flex-1 tw-text-left ${
                                            isDisabled ? 'tw-text-grey-200' : 'tw-text-grey-900'
                                        }`}>
                                            {submodel.name}
                                        </span>
                                        {hasFields && (
                                            <span className="tw-text-grey-600 tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center">
                                                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                            </span>
                                        )}
                                    </button>
                                </div>

                                {/* Submodel Fields */}
                                {isExpanded && submodel.fields.map(field => {
                                    const isAlreadySelected = isFieldAlreadySelected(field.id, field.submodelId);
                                    return (
                                        <div
                                            key={field.id}
                                            id={`field-mapping-field-${field.id}`}
                                            className={`tw-flex tw-items-center tw-h-7 tw-bg-grey-100 tw-border tw-border-grey-200/50 tw-pl-5 ${isAlreadySelected ? 'tw-opacity-50' : ''}`}
                                        >
                                            {/* Field content */}
                                            <button data-testid="inputparameter-fieldmappingdrawer-button-4"
                                                id={`field-mapping-field-btn-${field.id}`}
                                                onClick={() => !isAlreadySelected && handleFieldSelect(field)}
                                                disabled={isAlreadySelected}
                                                className={`tw-flex-1 tw-flex tw-items-center tw-gap-1.5 tw-h-full tw-pr-2 tw-transition-colors ${isAlreadySelected ? 'tw-cursor-not-allowed' : 'hover:tw-bg-blue-100 tw-cursor-pointer'}`}
                                                title={isAlreadySelected ? 'This field is already mapped to another parameter' : ''}
                                            >
                                                <span className="tw-w-3 tw-h-3 tw-flex tw-items-center tw-justify-center">
                                                    {getFieldIcon(field.type)}
                                                </span>
                                                <span className={`tw-text-caption-metadata tw-flex-1 tw-text-left ${isAlreadySelected ? 'tw-text-grey-400' : 'tw-text-grey-900'}`}>
                                                    {field.name}
                                                </span>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}

                    {/* Root Fields */}
                    {filteredData.rootFields.map(field => (
                        <div
                            key={field.id}
                            id={`field-mapping-root-field-${field.id}`}
                            className="tw-flex tw-items-center tw-h-7 tw-bg-white tw-border tw-border-grey-200/50"
                        >
                            {/* Field content */}
                            <button data-testid="inputparameter-fieldmappingdrawer-button-5"
                                id={`field-mapping-root-field-btn-${field.id}`}
                                onClick={() => handleFieldSelect(field)}
                                className="tw-flex-1 tw-flex tw-items-center tw-gap-1.5 tw-h-full tw-pr-2 tw-transition-colors hover:tw-bg-blue-100 tw-cursor-pointer"
                            >
                                <BoxIcon />
                                <span className="tw-text-caption-metadata tw-text-grey-900 tw-flex-1 tw-text-left">
                                    {field.name}
                                </span>
                                <span className="tw-text-grey-600 tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center">
                                    <ChevronRightIcon />
                                </span>
                            </button>
                        </div>
                    ))}

                    {/* Empty State */}
                    {filteredData.rootFields.length === 0 && filteredData.submodels.length === 0 && (
                        <div id="field-mapping-empty-state" className="tw-text-center tw-py-8">
                            <Typography variant="body-primary" className="tw-text-grey-600">
                                {searchTerm ? 'No matching fields found' : 'No fields available'}
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FieldMappingDrawer;
